import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { ChoiceChips } from "../design/ChoiceChips";
import { ROLE_LABELS } from "../sales/roles";
import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import {
  FEEDBACK_KINDS,
  FEEDBACK_STATUSES,
  type FeedbackKind,
  type FeedbackReportSummary,
  type FeedbackStatus,
} from "../types";
import {
  PILOT_NOTE_MAX,
  PILOT_REPORTS_PER_PAGE,
  buildPilotReportsFilter,
  buildReviewPatch,
  type ReviewStatus,
} from "./pilotReportsRules";

const RESOURCE = "feedback_reports_summary";

const formatDate = (iso: string) => new Date(iso).toLocaleString("es-MX");

/**
 * Almikia Epic PL (TASK-062): "Reportes del piloto" -- direccion-only (UI gate
 * here, RLS on `feedback_reports` underneath). Lists the problems and
 * suggestions sent from every screen, newest first, with status / kind
 * filters and the triage actions (Revisado, Resuelto, note).
 */
export const PilotReportsScreen = () => (
  <CanAccess
    resource={RESOURCE}
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <PilotReportsContent />
  </CanAccess>
);

PilotReportsScreen.path = "/pilot_reports";

const PilotReportsContent = () => {
  const translate = useTranslate();
  const [status, setStatus] = useState<FeedbackStatus | null>(null);
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [page, setPage] = useState(1);

  const { data, total, isPending, error, refetch } =
    useGetList<FeedbackReportSummary>(RESOURCE, {
      pagination: { page, perPage: PILOT_REPORTS_PER_PAGE },
      sort: { field: "created_at", order: "DESC" },
      filter: buildPilotReportsFilter(status, kind),
    });

  const reports = data ?? [];
  const pageCount = Math.max(
    1,
    Math.ceil((total ?? 0) / PILOT_REPORTS_PER_PAGE),
  );

  return (
    <div className="mt-2 max-w-2xl mx-auto space-y-4 pb-8">
      <h1 className="px-1 text-xl font-semibold">
        {translate("crm.pilotReports.title")}
      </h1>

      <div className="space-y-3">
        <ChoiceChips
          label={translate("crm.pilotReports.status_filter")}
          mode="single"
          value={status ? [status] : []}
          onChange={(ids) => {
            setStatus((ids[0] as FeedbackStatus | undefined) ?? null);
            setPage(1);
          }}
          options={FEEDBACK_STATUSES.map((id) => ({
            id,
            label: translate(`crm.pilotReports.status_${id}`),
          }))}
        />
        <ChoiceChips
          label={translate("crm.pilotReports.kind_filter")}
          mode="single"
          value={kind ? [kind] : []}
          onChange={(ids) => {
            setKind((ids[0] as FeedbackKind | undefined) ?? null);
            setPage(1);
          }}
          options={FEEDBACK_KINDS.map((id) => ({
            id,
            label: translate(`crm.feedback.kind_${id}`),
          }))}
        />
      </div>

      {error ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={() => refetch()}
        />
      ) : isPending ? (
        <ListCardsSkeleton rows={4} />
      ) : reports.length === 0 ? (
        <ListEmptyState title={translate("crm.pilotReports.empty")} />
      ) : (
        <ul className="space-y-3">
          {reports.map((report) => (
            <li key={report.id}>
              <ReportCard report={report} />
            </li>
          ))}
        </ul>
      )}

      {pageCount > 1 ? (
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            className="min-h-11"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {translate("crm.pilotReports.previous")}
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">
            {page} / {pageCount}
          </span>
          <Button
            variant="outline"
            className="min-h-11"
            disabled={page >= pageCount}
            onClick={() => setPage(page + 1)}
          >
            {translate("crm.pilotReports.next")}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const ReportCard = ({ report }: { report: FeedbackReportSummary }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [note, setNote] = useState(report.direccion_note ?? "");
  const [update, { isPending }] = useUpdate<FeedbackReportSummary>();

  const save = (
    patch: ReturnType<typeof buildReviewPatch>,
    successKey: string,
  ) =>
    update(
      "feedback_reports",
      { id: report.id, data: patch, previousData: report },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [RESOURCE] });
          notify(successKey, { type: "success" });
        },
        onError: () =>
          notify("crm.pilotReports.update_error", { type: "error" }),
      },
    );

  const setStatusTo = (next: ReviewStatus) =>
    save(
      buildReviewPatch(
        next,
        note !== (report.direccion_note ?? "") ? note : undefined,
      ),
      "crm.pilotReports.status_saved",
    );

  const reviewer = [report.reviewer_first_name, report.reviewer_last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium">
            {report.author_first_name} {report.author_last_name}
            <span className="text-muted-foreground">
              {" · "}
              {ROLE_LABELS[report.author_role] ?? report.author_role}
            </span>
          </span>
          <span className="text-sm text-muted-foreground tabular-nums">
            {formatDate(report.created_at)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          {translate(`crm.feedback.kind_${report.kind}`)}
          {" · "}
          {report.screen_title}
          {" ("}
          {report.screen_path}
          {")"}
          {" · "}
          {translate(`crm.pilotReports.status_${report.status}`)}
        </p>
        {/* Plain text only: React escapes it, never rendered as HTML. */}
        <p className="whitespace-pre-wrap break-words">{report.message}</p>
        {report.reviewed_at ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.pilotReports.reviewed_by", {
              name: reviewer || "—",
              date: formatDate(report.reviewed_at),
            })}
          </p>
        ) : null}

        <div className="space-y-1">
          <Label htmlFor={`pilot-note-${report.id}`}>
            {translate("crm.pilotReports.note_label")}
          </Label>
          <Textarea
            id={`pilot-note-${report.id}`}
            value={note}
            maxLength={PILOT_NOTE_MAX}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="min-h-11"
            disabled={isPending || note === (report.direccion_note ?? "")}
            onClick={() =>
              save(
                buildReviewPatch(undefined, note),
                "crm.pilotReports.note_saved",
              )
            }
          >
            {translate("crm.pilotReports.save_note")}
          </Button>
          <Button
            variant="outline"
            className="min-h-11"
            disabled={isPending || report.status === "revisado"}
            onClick={() => setStatusTo("revisado")}
          >
            {translate("crm.pilotReports.mark_revisado")}
          </Button>
          <Button
            className="min-h-11"
            disabled={isPending || report.status === "resuelto"}
            onClick={() => setStatusTo("resuelto")}
          >
            {translate("crm.pilotReports.mark_resuelto")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
